import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@groot/shell/lib/api";

export interface MomentRequestFile {
  id: number;
  momentRequestId: number;
  fileName: string;
  videoUrl: string;
  uploadedAt: string;
}

export interface MomentRequest {
  id: number;
  customerId: number;
  status: "pending" | "completed";
  publicLink: string;
  viewLink: string;
  createdAt: string;
  completedAt: string | null;
  videosClearedAt?: string | null;
  videosClearedNote?: string | null;
  videosClearedCount?: number | null;
  customer?: {
    id: number;
    name: string;
    mobileNumber: string;
  };
  videos?: MomentRequestFile[];
  _count?: {
    videos: number;
  };
}

export interface CreateMomentRequestDTO {
  customerId: number;
}

export interface UpdateMomentRequestDTO {
  id: number;
  data: {
    status?: "pending" | "completed";
  };
}

export interface UploadMomentVideoDTO {
  momentRequestId: number;
  fileName: string;
  videoUrl: string;
}

export interface ClearVideosDTO {
  momentRequestId: number;
  note: string;
}

export function useMomentRequests() {
  return useQuery({
    queryKey: ["moment-requests"],
    queryFn: async () => {
      return apiClient.get<MomentRequest[]>("/moment-requests");
    },
  });
}

export function useMomentRequest(id: number) {
  return useQuery({
    queryKey: ["moment-requests", id],
    queryFn: async () => {
      return apiClient.get<MomentRequest>(`/moment-requests/${id}`);
    },
    enabled: !!id,
  });
}

export function useMomentRequestByPublicLink(publicLink: string) {
  return useQuery({
    queryKey: ["moment-requests", "public", publicLink],
    queryFn: async () => {
      return apiClient.get<MomentRequest>(`/public/moment-requests/${publicLink}`);
    },
    enabled: !!publicLink,
  });
}

export function useCreateMomentRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateMomentRequestDTO) => {
      return apiClient.post<MomentRequest>("/moment-requests", input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["moment-requests"] });
    },
  });
}

export function useUpdateMomentRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: UpdateMomentRequestDTO) => {
      return apiClient.put<MomentRequest>(`/moment-requests/${id}`, data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["moment-requests"] });
      queryClient.invalidateQueries({ queryKey: ["moment-requests", variables.id] });
    },
  });
}

export function useDeleteMomentRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/moment-requests/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["moment-requests"] });
    },
  });
}

export function useAddMomentVideo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ momentRequestId, fileName, videoUrl }: UploadMomentVideoDTO) => {
      return apiClient.post<MomentRequestFile>(`/moment-requests/${momentRequestId}/files`, {
        fileName,
        videoUrl,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["moment-requests"] });
      queryClient.invalidateQueries({ queryKey: ["moment-requests", variables.momentRequestId] });
      queryClient.invalidateQueries({ queryKey: ["moment-requests", "public"] });
    },
  });
}

export function usePublicUploadVideo(publicLink: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { file: File; contentType?: string }) => {
      const formData = new FormData();
      formData.append("file", params.file);
      return apiClient.postForm<MomentRequestFile>(
        `/public/moment-requests/${publicLink}/files`,
        formData,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["moment-requests", "public", publicLink] });
    },
  });
}

export function usePublicDeleteVideo(publicLink: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ videoId }: { videoId: number }) => {
      await apiClient.delete(`/public/moment-requests/${publicLink}/files/${videoId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["moment-requests", "public", publicLink] });
    },
  });
}

export function useDeleteMomentVideo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      momentRequestId,
      videoId,
    }: {
      momentRequestId: number;
      videoId: number;
    }) => {
      await apiClient.delete(`/moment-requests/${momentRequestId}/files/${videoId}`);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["moment-requests"] });
      queryClient.invalidateQueries({ queryKey: ["moment-requests", variables.momentRequestId] });
      queryClient.invalidateQueries({ queryKey: ["moment-requests", "public"] });
    },
  });
}

export function useClearVideos() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ momentRequestId, note }: ClearVideosDTO) => {
      return apiClient.post<MomentRequest>(`/moment-requests/${momentRequestId}/clear-videos`, {
        note,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["moment-requests"] });
      queryClient.invalidateQueries({ queryKey: ["moment-requests", variables.momentRequestId] });
    },
  });
}
